import * as React from 'react';
import { ReactComponent as ArchiproReceiptMark } from '../../icons/svg/archipro-receipt-mark.svg';

/**
 * Built-in left pane: full-bleed photo, light scrim, white receipt mark, and an
 * optional statement. Positions use the numeric scale so they resolve correctly
 * in Business Manager (legacy rem ≈ 1px) and on the public site.
 *
 * Private to `two-pane-modal`: consumers reach it through the modal's
 * `paneImage` / `paneStatement` props, never as a component of its own.
 */
const BrandPane = ({
    image,
    statement,
}: {
    image: string;
    statement?: string;
}) => (
    <div className="relative h-full w-full">
        <img
            src={image}
            alt=""
            className="absolute inset-0 size-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <ArchiproReceiptMark
            aria-hidden
            // Figma ~112×84 at inset 28×42 → scale steps (px/4), same as card sizing.
            className="absolute top-10.5 left-7 h-auto w-28 [&_path]:fill-white"
        />
        {statement ? (
            // Statement ~28px (step 7). Width ~373; bottom ≈ 29px → bottom-7.25 (design-px/4).
            // Bang on text/leading: BM portals outside `.bm-v2`, so unlayered `p` would win.
            <p className="absolute bottom-7.25 left-7 max-w-93.25 text-7! leading-[1.211]! font-medium tracking-tight text-white">
                {statement}
            </p>
        ) : null}
    </div>
);

/**
 * Resolves the left-pane content: a custom `pane` wins; otherwise a brand photo
 * pane is built from `paneImage` / `paneStatement`.
 */
export const resolvePane = (
    pane: React.ReactNode | undefined,
    paneImage: string | undefined,
    paneStatement: string | undefined,
): React.ReactNode | undefined => {
    if (pane !== undefined && pane !== null) {
        return pane;
    }
    if (!paneImage) {
        return undefined;
    }
    return <BrandPane image={paneImage} statement={paneStatement} />;
};
